import { RestClient, RestError } from "./restClient";

/** One row in the per-tenant marketplace listing. ``installed`` flips to
 *  true when the tenant already has the package in egpt_company_plugins. */
export interface MarketplacePackage {
  name: string;
  display_name: string;
  description: string;
  category: string;
  version: string;
  ring: 1 | 2 | 3;
  publisher: string | null;
  installed: boolean;
  components?: {
    skills: string[];
    scripts: string[];
    commands: string[];
    mcp_servers: string[];
  };
  selected?: Record<string, string[]>;
  /** Whether this package may be installed more than once (two Jira boards,
   *  two GitHub orgs). Gating "Add another" on a field the server does not
   *  send is how the button appears for connectors that cannot support it. */
  multi_install?: boolean;
  /** Every install of this package visible to the caller. One entry for a
   *  normal install; more once it has been installed again. */
  instances?: MarketplaceInstance[];
}

/** One install of a package. `install_id` is the install's identity (V70);
 *  `label` is what the user called it ("Jira — EU board"). */
export interface MarketplaceInstance {
  install_id: string;
  label: string | null;
  selected?: Record<string, string[]> | null;
}

/** Wraps plugin_platform_service's marketplace browse + install-from-registry
 *  surfaces. The list endpoint is flag-gated (MARKETPLACE_BROWSE_ENABLED)
 *  on the backend — 403/404 are treated as "marketplace disabled" so the
 *  UI renders an empty state instead of an error. */
export class MarketplaceApi {
  constructor(private client: RestClient) {}

  /** GET /v1/marketplace — returns [] on 403 (flag off) or 404 (route
   *  not yet deployed). All other errors propagate.
   *
   *  The server returns sectioned rows (`{sections: [{key, title, plugins}]}`);
   *  a plugin appears in its category section and again under "installed", so
   *  the flatten dedupes by name, first occurrence winning. The older flat
   *  `{packages: [...]}` shape is still accepted so this client works against
   *  a deployment on either side of that change. */
  async list(): Promise<MarketplacePackage[]> {
    try {
      const r = await this.client.get<{
        packages?: MarketplacePackage[];
        sections?: Array<{ plugins?: MarketplacePackage[] }>;
      }>("/plugins/v1/marketplace");

      if (r.packages) return r.packages;

      const seen = new Set<string>();
      const flattened: MarketplacePackage[] = [];
      for (const section of r.sections ?? []) {
        for (const pkg of section.plugins ?? []) {
          if (seen.has(pkg.name)) continue;
          seen.add(pkg.name);
          flattened.push(pkg);
        }
      }
      return flattened;
    } catch (e: unknown) {
      if (e instanceof RestError && (e.status === 403 || e.status === 404)) {
        return [];
      }
      throw e;
    }
  }

  /** POST /v1/plugins/{name}/install-from-registry — SP-E Task 6 endpoint
   *  with signature + listing-state verification. */
  install(
    name: string,
    pinnedVersion?: string,
    config: Record<string, unknown> = {},
    opts: {
      additional?: boolean;
      label?: string;
      /** "company" → every member sees it (admin only); "user" → just the
       *  installer. Omitted defers to the manifest's recommendation, which
       *  is what every caller did before this existed. */
      scope?: "company" | "user";
    } = {},
  ): Promise<{ status: string; name: string }> {
    return this.client.post<{ status: string; name: string }>(
      `/plugins/v1/plugins/${encodeURIComponent(name)}/install-from-registry`,
      {
        pinned_version: pinnedVersion,
        config,
        // Omitted rather than false so an older backend, which would reject
        // an unknown field, still accepts a normal install.
        ...(opts.additional ? { additional: true } : {}),
        ...(opts.label ? { label: opts.label } : {}),
        ...(opts.scope ? { scope: opts.scope } : {}),
      },
    );
  }

  /** PATCH /v1/plugins/{name}/config — updates the active component
   *  selection for an installed plugin. */
  updateConfig(
    name: string,
    components: Record<string, string[]>,
  ): Promise<{ status: string; name: string; components: Record<string, string[]> }> {
    return this.client.patch<{ status: string; name: string; components: Record<string, string[]> }>(
      `/plugins/v1/plugins/${encodeURIComponent(name)}/config`,
      { components },
    );
  }

  /** DELETE /v1/plugins/{name} — removes the plugin from the tenant's
   *  installed set (admin-gated on the backend). */
  uninstall(name: string): Promise<void> {
    return this.client.delete<void>(
      `/plugins/v1/plugins/${encodeURIComponent(name)}`,
    );
  }
}
